import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fetchCompanyBranding } from '../services/companyCustomizationApi';
import type { CompanyBranding } from '../services/companyCustomizationApi';
import { useCompany } from './CompanyContext';

type BrandingContextValue = {
  branding: CompanyBranding | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

const defaultBrandingContext: BrandingContextValue = {
  branding: null,
  isLoading: false,
  refresh: async () => undefined,
};

const BrandingContext = createContext<BrandingContextValue>(defaultBrandingContext);

const DEFAULT_BRANDING: CompanyBranding = {
  primaryColor: '#4f46e5',
  secondaryColor: '#4338ca',
  accentColor: '#3b82f6',
};

const STYLE_TAG_ID = 'company-custom-css';

export const BrandingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { currentCompany } = useCompany();
  const [branding, setBranding] = useState<CompanyBranding | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const defaultFavicon = useRef<string | null>(null);

  const applyBrandingToDocument = useCallback((data: CompanyBranding | null) => {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    const primary = data?.primaryColor || DEFAULT_BRANDING.primaryColor!;
    const secondary = data?.secondaryColor || DEFAULT_BRANDING.secondaryColor!;
    const accent = data?.accentColor || DEFAULT_BRANDING.accentColor!;

    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-secondary', secondary);
    root.style.setProperty('--brand-accent', accent);

    if (data?.logoUrl) {
      root.style.setProperty('--brand-logo-url', `url(${data.logoUrl})`);
    } else {
      root.style.removeProperty('--brand-logo-url');
    }

    const styleTag =
      (document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null) ||
      (() => {
        const created = document.createElement('style');
        created.id = STYLE_TAG_ID;
        document.head.appendChild(created);
        return created;
      })();

    styleTag.textContent = data?.customCss || '';

    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
    if (favicon) {
      if (!defaultFavicon.current) {
        defaultFavicon.current = favicon.href;
      }
      favicon.href = data?.faviconUrl || defaultFavicon.current || favicon.href;
    }
  }, []);

  const loadBranding = useCallback(async () => {
    if (!currentCompany) {
      setBranding(null);
      applyBrandingToDocument(null);
      return;
    }

    setIsLoading(true);
    try {
      const data = await fetchCompanyBranding(currentCompany.id);
      setBranding(data);
      applyBrandingToDocument(data);
    } catch (error) {
      console.error('[BrandingProvider] Failed to load branding', error);
      setBranding(null);
      applyBrandingToDocument(null);
    } finally {
      setIsLoading(false);
    }
  }, [applyBrandingToDocument, currentCompany]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  const value = useMemo<BrandingContextValue>(
    () => ({
      branding,
      isLoading,
      refresh: loadBranding,
    }),
    [branding, isLoading, loadBranding],
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = (): BrandingContextValue => {
  return useContext(BrandingContext);
};

